/**
 * OrderSummary — Totals bar for the checkout.
 * Shows subtotal, discount, tax, and grand total.
 */

import React from 'react';
import { Box, Typography, Divider } from '@mui/material';

function formatCents(cents) {
  if (cents == null) return '—';
  const negative = cents < 0;
  return `${negative ? '-' : ''}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

export default function OrderSummary({ totals, compact = false }) {
  if (!totals) return null;

  if (compact) {
    return (
      <Box sx={{ pt: 0.5 }}>
        {totals.discount_cents > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
            <Typography variant="caption" color="success.main">Discount</Typography>
            <Typography variant="caption" color="success.main">{formatCents(-totals.discount_cents)}</Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
          <Typography variant="caption" color="text.secondary">Tax</Typography>
          <Typography variant="caption" color="text.secondary">{formatCents(totals.tax_cents || 0)}</Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body1" fontWeight={700}>Total</Typography>
          <Typography variant="body1" fontWeight={700}>{formatCents(totals.grand_total_cents)}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">Subtotal</Typography>
        <Typography variant="body2">{formatCents(totals.subtotal_cents)}</Typography>
      </Box>
      {totals.discount_cents > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="success.main">Discount</Typography>
          <Typography variant="body2" color="success.main">{formatCents(-totals.discount_cents)}</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">Tax</Typography>
        <Typography variant="body2">{formatCents(totals.tax_cents || 0)}</Typography>
      </Box>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700}>Total</Typography>
        <Typography variant="h6" fontWeight={700}>{formatCents(totals.grand_total_cents)}</Typography>
      </Box>
      {totals.items_deferred > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {totals.items_deferred} item(s) take effect at end of billing period
        </Typography>
      )}
    </Box>
  );
}
